import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  REPORT_PRESETS,
  REPORT_PRESET_LABEL,
  averageOrder,
  changeText,
  comparisonPeriod,
  csvFileName,
  customPeriodError,
  hourLabel,
  hoursOfDay,
  isQuietPeriod,
  methodsTable,
  paymentVoidsByDayTable,
  paymentVoidsTable,
  percentChange,
  periodLength,
  presetPeriod,
  refundsByDayTable,
  refundsTable,
  salesByDayTable,
  salesByHourTable,
  salesByWeekdayTable,
  salesVsTakingsTable,
  shiftDate,
  tableToCsv,
  weekdays,
  writeOffsTable,
  type ReportMoney,
  type ReportPeriod,
  type ReportPreset,
  type ReportTable,
} from '@didto/domain';
import {
  Button,
  EmptyState,
  Field,
  IconClipboard,
  IconPulse,
  IconWallet,
  Input,
  KvRow,
  MetricCard,
  PageHeader,
  Section,
  SegmentedControl,
  Select,
  Skeleton,
} from '@didto/ui';
import { useOwnerReview } from '@/app/ownerReview';
import { useSession } from '@/app/session';
import { fetchReportMoney, reportKeys } from '@/lib/reports';
import { downloadText } from '@/lib/download';
import { ReadError, useFormats } from '@/routes/shifts/reviewItems';
import { BarChart, type ChartBar } from './BarChart';
import './reports.css';

/*
 * The owner's money reports (Phase 8A, ADR-043): a period, four figures, then one section per question, each opening
 * a detail with its table and CSV file. Every figure is the server's (public.report_money); the page adds nothing but
 * arithmetic on them (an average, a percentage, a difference) and never invents one.
 */

const VIEWS = [
  ['overview', 'Overview'],
  ['days', 'Sales by day'],
  ['hours', 'Sales by hour'],
  ['weekdays', 'Sales by weekday'],
  ['svt', 'Sales and takings'],
  ['methods', 'Payment methods'],
  ['refunds', 'Refunds'],
  ['voids', 'Payment voids'],
  ['writeoffs', 'Write-offs'],
] as const;
type View = (typeof VIEWS)[number][0];
const VIEW_KEYS = VIEWS.map(([v]) => v) as readonly string[];

const SALES_MEANS =
  'Sales: the goods of each day’s orders as they stand now — every order not cancelled or voided, paid or not, less anything returned.';
const TAKINGS_MEANS =
  'Takings: money received less money given back, on the day of the shift that recorded it (the Day view’s figures), less refunds made outside a shift.';

function fmtDate(date: string, withYear: boolean): string {
  const d = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString('en-PH', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}

function periodText(p: ReportPeriod, withYear: boolean): string {
  return p.from === p.to
    ? fmtDate(p.from, withYear)
    : `${fmtDate(p.from, withYear)} – ${fmtDate(p.to, withYear)}`;
}

export function ReportsPage() {
  const { locationId } = useOwnerReview();
  const [params, setParams] = useSearchParams();
  const preset: ReportPreset = (REPORT_PRESETS as readonly string[]).includes(params.get('p') ?? '')
    ? (params.get('p') as ReportPreset)
    : 'today';
  const view: View = VIEW_KEYS.includes(params.get('view') ?? '')
    ? (params.get('view') as View)
    : 'overview';
  const set = (next: Record<string, string | null>, push = false) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(next)) {
          if (v == null) p.delete(k);
          else p.set(k, v);
        }
        return p;
      },
      { replace: !push },
    );

  // today's report first: it is also how the page learns the location's business date
  const todayQ = useQuery({
    queryKey: reportKeys.today(locationId),
    queryFn: () => fetchReportMoney(locationId, null, null),
    refetchInterval: 60_000,
  });
  const today = todayQ.data?.today ?? null;

  const customFrom = params.get('from') ?? '';
  const customTo = params.get('to') ?? '';
  const customError =
    preset === 'custom' && today ? customPeriodError(customFrom, customTo, today) : null;
  const period: ReportPeriod | null = !today
    ? null
    : preset === 'custom'
      ? customError
        ? null
        : { from: customFrom, to: customTo }
      : presetPeriod(preset, today);
  const isToday = preset === 'today';
  const liveToday = !!period && !!today && period.to === today;

  const periodQ = useQuery({
    queryKey: period
      ? reportKeys.period(locationId, period.from, period.to)
      : ['report-money', 'none'],
    queryFn: () => fetchReportMoney(locationId, period!.from, period!.to),
    enabled: !!period && !isToday,
    refetchInterval: liveToday ? 60_000 : false,
  });
  const q = isToday ? todayQ : periodQ;
  const r =
    q.data && period && q.data.from === period.from && q.data.to === period.to ? q.data : undefined;

  const cmp = period ? comparisonPeriod(preset, period) : null;
  const cmpQ = useQuery({
    queryKey: cmp ? reportKeys.period(locationId, cmp.from, cmp.to) : ['report-money', 'none-cmp'],
    queryFn: () => fetchReportMoney(locationId, cmp!.from, cmp!.to),
    enabled: !!cmp && view === 'overview',
    staleTime: 5 * 60_000,
  });

  const pageRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const shownView = useRef(view);
  // a new view starts at the top, with the focus on it (the control that opened it is gone)
  useEffect(() => {
    if (shownView.current === view) return;
    shownView.current = view;
    pageRef.current?.scrollIntoView({ block: 'start' });
    bodyRef.current?.focus({ preventScroll: true });
  }, [view]);

  const withYear =
    !!period &&
    (period.from.slice(0, 4) !== period.to.slice(0, 4) ||
      (today ?? '').slice(0, 4) !== period.to.slice(0, 4));
  const subtitle = period
    ? `${REPORT_PRESET_LABEL[preset]} · ${periodText(period, withYear)}`
    : preset === 'custom'
      ? 'Custom · choose two dates'
      : 'Loading the business date…';

  return (
    <div className="d-page rp" data-testid="reports-page" ref={pageRef}>
      <PageHeader eyebrow="Owner" title="Reports" subtitle={subtitle} />
      <div className="rp-controls">
        <div className="rp-period">
          <SegmentedControl
            ariaLabel="Period"
            testId="report-period"
            value={preset}
            options={REPORT_PRESETS.map((p) => ({ value: p, label: REPORT_PRESET_LABEL[p] }))}
            onChange={(p) =>
              p === 'custom' && today
                ? set({ p, from: customFrom || shiftDate(today, -6), to: customTo || today })
                : set({ p, from: null, to: null })
            }
          />
        </div>
        {preset === 'custom' && today && (
          <CustomPeriod
            key={`${customFrom}/${customTo}`}
            from={customFrom}
            to={customTo}
            today={today}
            error={customError}
            onApply={(from, to) => set({ from, to })}
          />
        )}
        <Field label="Report">
          {(id) => (
            <Select
              id={id}
              value={view}
              data-testid="report-view"
              onChange={(e) =>
                set({ view: e.target.value === 'overview' ? null : e.target.value }, true)
              }
            >
              {VIEWS.map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <ReadError
        error={q.error ?? todayQ.error}
        hasData={!!r}
        onRetry={() => {
          void todayQ.refetch();
          if (!isToday) void periodQ.refetch();
        }}
        testId="report-error"
      />
      {!r && !q.error && !todayQ.error && (preset !== 'custom' || !customError) && (
        <Skeleton kind="cards" count={4} label="Loading the report" />
      )}
      <div ref={bodyRef} tabIndex={-1} className="rp-body" aria-label="Report">
        {r && period && (
          <ReportBody
            r={r}
            prev={view === 'overview' ? cmpQ.data : undefined}
            cmpLabel={cmp?.label ?? ''}
            cmpWhen={cmp?.when ?? ''}
            view={view}
            period={period}
            withYear={withYear}
            onView={(v) => set({ view: v === 'overview' ? null : v }, true)}
            live={liveToday}
          />
        )}
      </div>
    </div>
  );
}

function CustomPeriod({
  from,
  to,
  today,
  error,
  onApply,
}: {
  from: string;
  to: string;
  today: string;
  error: string | null;
  onApply: (from: string, to: string) => void;
}) {
  const [f, setF] = useState(from);
  const [t, setT] = useState(to);
  const draftError = customPeriodError(f, t, today);
  return (
    <form
      className="rp-custom"
      onSubmit={(e) => {
        e.preventDefault();
        if (!draftError) onApply(f, t);
      }}
    >
      <Field label="From">
        {(id) => (
          <Input
            id={id}
            type="date"
            value={f}
            max={today}
            onChange={(e) => setF(e.target.value)}
            data-testid="report-from"
          />
        )}
      </Field>
      <Field label="To" error={draftError ?? (f === from && t === to ? error : null)}>
        {(id) => (
          <Input
            id={id}
            type="date"
            value={t}
            max={today}
            onChange={(e) => setT(e.target.value)}
            data-testid="report-to"
          />
        )}
      </Field>
      <Button type="submit" variant="primary" disabled={!!draftError} data-testid="report-apply">
        Show
      </Button>
    </form>
  );
}

function ReportBody({
  r,
  prev,
  cmpLabel,
  cmpWhen,
  view,
  period,
  withYear,
  onView,
  live,
}: {
  r: ReportMoney;
  prev: ReportMoney | undefined;
  cmpLabel: string;
  cmpWhen: string;
  view: View;
  period: ReportPeriod;
  withYear: boolean;
  onView: (v: View) => void;
  /** The period includes today, a day still running. */
  live: boolean;
}) {
  const f = useFormats();
  const s = useSession();
  const currency = s.me?.business?.currency ?? 'PHP';
  const t = r.totals;
  const quiet = isQuietPeriod(t, r.write_offs);
  // -0 (a zero given back, negated) reads as 0, never "-₱0.00"
  const money = (minor: number | null | undefined) => f.money(minor === 0 ? 0 : minor);
  const ctx: Ctx = { r, period, withYear, currency, money, onView, live };

  if (view !== 'overview')
    return (
      <>
        <button
          type="button"
          className="d-btn d-btn--sm d-btn--ghost rp-back"
          onClick={() => onView('overview')}
        >
          ← Overview
        </button>
        <Detail view={view} c={ctx} quiet={quiet} />
      </>
    );

  const avg = averageOrder(t.sales_minor, t.orders);
  const prevAvg = prev ? averageOrder(prev.totals.sales_minor, prev.totals.orders) : null;
  const Label = cmpLabel.charAt(0).toUpperCase() + cmpLabel.slice(1);
  // a period still running is never given a percentage against a finished one: the earlier figure is shown as it was
  const note = (
    cur: number | null,
    before: number | null | undefined,
    noun: string,
    show: (v: number) => string,
  ) => {
    if (!prev || before == null) return '\u00a0';
    if (live || before < 0) return `${Label}: ${show(before)}`;
    if (before === 0) return `No ${noun} ${cmpWhen}`;
    const pct = cur == null ? null : percentChange(cur, before);
    return pct == null ? `${Label}: ${show(before)}` : `${changeText(pct)} vs ${cmpLabel}`;
  };

  return (
    <>
      <div className="d-metrics rp-metrics">
        <MetricCard
          icon={<IconClipboard />}
          label="Sales"
          value={<span className="d-mono">{money(t.sales_minor)}</span>}
          note={note(t.sales_minor, prev?.totals.sales_minor, 'sales', money)}
          testId="report-sales"
        />
        <MetricCard
          icon={<IconWallet />}
          label="Takings"
          value={<span className="d-mono">{money(t.takings_minor)}</span>}
          note={note(t.takings_minor, prev?.totals.takings_minor, 'takings', money)}
          testId="report-takings"
        />
        <MetricCard
          icon={<IconPulse />}
          label="Orders"
          value={t.orders}
          note={note(t.orders, prev?.totals.orders, 'orders', String)}
          testId="report-orders"
        />
        <MetricCard
          label="Average order"
          value={<span className="d-mono">{avg == null ? '—' : money(avg)}</span>}
          note={
            avg == null
              ? live
                ? 'No orders yet'
                : 'No orders'
              : note(avg, prevAvg, 'orders', money)
          }
          testId="report-average"
        />
      </div>

      {quiet ? (
        <EmptyState title={emptyTitle(live)} testId="report-empty">
          Sales and takings appear here as soon as the counter records them. Nothing is estimated.
        </EmptyState>
      ) : (
        <>
          <Trend c={ctx} />
          <SalesAndTakings c={ctx} compact />
          <PaymentMix c={ctx} />
          <GivenBack c={ctx} />
        </>
      )}
    </>
  );
}

function emptyTitle(live: boolean): string {
  return live ? 'No sales recorded for this period yet.' : 'No sales recorded in this period.';
}

interface Ctx {
  live: boolean;
  r: ReportMoney;
  period: ReportPeriod;
  withYear: boolean;
  currency: string;
  money: (minor: number | null | undefined) => string;
  onView: (v: View) => void;
}

function Details({ c, to, label }: { c: Ctx; to: View; label: string }) {
  const name = VIEWS.find(([v]) => v === to)?.[1] ?? '';
  return (
    <button
      type="button"
      className="d-btn d-btn--sm d-btn--ghost"
      aria-label={`${label}: ${name}`}
      onClick={() => c.onView(to)}
      data-testid={`report-open-${to}`}
    >
      {label} →
    </button>
  );
}

function CsvButton<T>({ table, c }: { table: ReportTable<T>; c: Ctx }) {
  return (
    <Button
      size="sm"
      variant="secondary"
      data-testid={`csv-${table.name}`}
      aria-label={`Download ${table.name.replace(/-/g, ' ')} as CSV`}
      onClick={() => downloadText(csvFileName(table.name, c.period), tableToCsv(table, c.currency))}
    >
      CSV
    </Button>
  );
}

/** A table from its one description: the same rows and columns the CSV file writes. Dates open that day's Day view. */
function ReportTableView<T>({
  table,
  c,
  caption,
  id,
}: {
  table: ReportTable<T>;
  c: Ctx;
  caption: string;
  id?: string;
}) {
  const cell = (kind: string, v: string | number | null, total: boolean): ReactNode => {
    if (v == null) return '—';
    if (kind === 'money') return <span className="d-mono">{c.money(Number(v))}</span>;
    if (kind === 'date' && !total)
      return (
        <Link to={`/shifts/day/${v}`} className="rp-day-link">
          {fmtDate(String(v), c.withYear)}
        </Link>
      );
    return v;
  };
  return (
    <div className="d-table-scroll rp-table">
      <table className="d-table" id={id} data-testid={`table-${table.name}`}>
        <caption className="d-sr-only">{caption}</caption>
        <thead>
          <tr>
            {table.columns.map((col) => (
              <th
                key={col.header}
                scope="col"
                className={col.kind === 'money' || col.kind === 'count' ? 'd-num' : undefined}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, i) => (
            <tr key={i}>
              {table.columns.map((col, j) => {
                const Cell = j === 0 ? 'th' : 'td';
                return (
                  <Cell
                    key={col.header}
                    scope={j === 0 ? 'row' : undefined}
                    className={col.kind === 'money' || col.kind === 'count' ? 'd-num' : undefined}
                  >
                    {cell(col.kind, col.value(row), false)}
                  </Cell>
                );
              })}
            </tr>
          ))}
        </tbody>
        {table.total && (
          <tfoot>
            <tr className="rp-total" data-testid={`total-${table.name}`}>
              {table.columns.map((col, j) => {
                const Cell = j === 0 ? 'th' : 'td';
                return (
                  <Cell
                    key={col.header}
                    scope={j === 0 ? 'row' : undefined}
                    className={col.kind === 'money' || col.kind === 'count' ? 'd-num' : undefined}
                  >
                    {cell(col.kind, col.value(table.total!), true)}
                  </Cell>
                );
              })}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function shortDate(date: string, withYear: boolean): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-PH', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: '2-digit' } : {}),
    timeZone: 'UTC',
  });
}

/**
 * The period's Sales (and Takings) as bars a phone can read: one per day up to 62 days, then one per week (from
 * Monday), then one per month. Each bar is the sum of its days, so the bars still add up to the period.
 */
function trendBars(c: Ctx): { bars: ChartBar[]; unit: 'Day' | 'Week' | 'Month' } {
  const n = c.r.days.length;
  const unit = n <= 62 ? 'Day' : n <= 186 ? 'Week' : 'Month';
  const keyOf = (date: string) => {
    if (unit === 'Day') return date;
    if (unit === 'Month') return date.slice(0, 7);
    const dow = new Date(`${date}T12:00:00Z`).getUTCDay();
    return shiftDate(date, -((dow + 6) % 7));
  };
  const out = new Map<string, ChartBar>();
  for (const d of c.r.days) {
    const k = keyOf(d.date);
    const b = out.get(k);
    if (b) {
      b.value += d.sales_minor;
      b.value2 = (b.value2 ?? 0) + d.takings_minor;
      continue;
    }
    const label =
      unit === 'Month'
        ? new Date(`${k}-15T12:00:00Z`).toLocaleDateString('en-PH', {
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC',
          })
        : unit === 'Week'
          ? `Wk ${shortDate(k, c.withYear)}`
          : shortDate(k, c.withYear);
    out.set(k, { key: k, label, value: d.sales_minor, value2: d.takings_minor });
  }
  return { bars: [...out.values()], unit };
}

function hourBars(c: Ctx): ChartBar[] {
  return hoursOfDay(c.r.by_hour, c.r.day_start).map((h) => ({
    key: String(h.hour),
    label: hourLabel(h.hour).slice(0, 5),
    value: h.sales_minor,
  }));
}

function Trend({ c }: { c: Ctx }) {
  const oneDay = periodLength(c.period) === 1;
  return oneDay ? (
    <Section
      title="Sales by hour"
      description="When the day’s orders were placed (an order from paper counts at the time on the paper)."
      actions={<Details c={c} to="hours" label="Details" />}
      testId="report-trend"
    >
      {c.r.by_hour.length === 0 ? (
        <EmptyState title={emptyTitle(c.live)} />
      ) : (
        <BarChart
          title="Sales by hour"
          bars={hourBars(c)}
          format={c.money}
          series={['Sales']}
          labelHeader="Hour"
        />
      )}
    </Section>
  ) : (
    <Section
      title={`Sales by ${trendBars(c).unit.toLowerCase()}`}
      description={SALES_MEANS}
      actions={<Details c={c} to="days" label="Details" />}
      testId="report-trend"
    >
      {c.r.totals.sales_minor === 0 ? (
        <EmptyState title={emptyTitle(c.live)} />
      ) : (
        <TrendChart c={c} />
      )}
    </Section>
  );
}

/** Sales (and Takings) over the period, by day, week or month; its numbers in the page's table when by day. */
function TrendChart({ c, two, tableId }: { c: Ctx; two?: boolean; tableId?: string }) {
  const { bars, unit } = trendBars(c);
  return (
    <BarChart
      title={`${two ? 'Sales and takings' : 'Sales'} by ${unit.toLowerCase()}`}
      bars={bars}
      format={c.money}
      series={two ? ['Sales', 'Takings'] : ['Sales']}
      tableId={unit === 'Day' ? tableId : undefined}
      labelHeader={unit}
    />
  );
}

function SalesAndTakings({ c, compact }: { c: Ctx; compact?: boolean }) {
  const t = c.r.totals;
  return (
    <Section
      title="Sales and takings"
      description="Two different figures, never added together."
      actions={
        compact ? (
          <Details c={c} to="svt" label="Details" />
        ) : (
          <CsvButton table={salesVsTakingsTable(c.r)} c={c} />
        )
      }
      testId="report-svt"
    >
      <div className="d-kv">
        <KvRow
          label="Sales"
          value={<span className="d-mono">{c.money(t.sales_minor)}</span>}
          testId="svt-sales"
        />
        <KvRow
          label="Takings"
          value={<span className="d-mono">{c.money(t.takings_minor)}</span>}
          testId="svt-takings"
        />
        <KvRow
          total
          label="Takings less sales"
          value={<span className="d-mono">{c.money(t.takings_minor - t.sales_minor)}</span>}
          testId="svt-difference"
        />
      </div>
      <p className="d-muted rp-why">
        {t.owed_minor > 0 && (
          <>Of the sales, {c.money(t.owed_minor)} is still open on these orders. </>
        )}
        {t.written_off_on_orders_minor > 0 && (
          <>{c.money(t.written_off_on_orders_minor)} was written off on them. </>
        )}
        Takings count money on the day it was taken or given back, whichever day its order was.
      </p>
      {!compact && (
        <>
          <h3 className="rp-sub">How the takings add up</h3>
          <div className="d-kv">
            <KvRow
              label="Received"
              value={<span className="d-mono">{c.money(t.captured_minor)}</span>}
              testId="svt-received"
            />
            <KvRow
              label="Payments voided"
              value={<span className="d-mono">{c.money(-t.voided_minor)}</span>}
              testId="svt-voided"
            />
            <KvRow
              label="Refunded in shifts"
              value={<span className="d-mono">{c.money(-t.refunded_minor)}</span>}
              testId="svt-refunded"
            />
            <KvRow
              label="Refunded outside a shift"
              value={<span className="d-mono">{c.money(-t.outside_refunded_minor)}</span>}
              testId="svt-outside"
            />
            <KvRow
              total
              label="Takings"
              value={<span className="d-mono">{c.money(t.takings_minor)}</span>}
            />
          </div>
          <p className="d-muted rp-why">{SALES_MEANS}</p>
          <p className="d-muted rp-why">{TAKINGS_MEANS}</p>
          {periodLength(c.period) > 1 && <TrendChart c={c} two tableId="table-svt" />}
          <ReportTableView
            table={salesVsTakingsTable(c.r)}
            c={c}
            caption="Sales and takings by day"
            id="table-svt"
          />
        </>
      )}
    </Section>
  );
}

function PaymentMix({ c }: { c: Ctx }) {
  const rows = c.r.methods.filter((m) => m.net_minor !== 0 || m.count > 0);
  const positive = rows.reduce((n, m) => n + Math.max(m.net_minor, 0), 0);
  const outside = c.r.totals.outside_refunded_minor;
  return (
    <Section
      title="Payment methods"
      description="Net of each method: received less voided less refunded."
      actions={<Details c={c} to="methods" label="Details" />}
      testId="report-mix"
    >
      {rows.length === 0 && outside === 0 ? (
        <EmptyState title="No payments recorded for this period yet." />
      ) : (
        <ul className="rp-mix">
          {rows.map((m) => {
            const share =
              positive > 0 && m.net_minor > 0 ? Math.round((m.net_minor / positive) * 100) : null;
            return (
              <li key={`${m.method}/${m.kind}`} className="rp-mix__row">
                <span className="rp-mix__name">{m.method}</span>
                <span className="rp-mix__bar" aria-hidden>
                  <span style={{ width: `${share ?? 0}%` }} />
                </span>
                <span className="rp-mix__value d-mono">{c.money(m.net_minor)}</span>
                <span className="rp-mix__share">{share == null ? '—' : `${share}%`}</span>
              </li>
            );
          })}
          {outside !== 0 && (
            <li className="rp-mix__row">
              <span className="rp-mix__name">Refunded outside a shift</span>
              <span className="rp-mix__bar" aria-hidden />
              <span className="rp-mix__value d-mono">{c.money(-outside)}</span>
              <span className="rp-mix__share">—</span>
            </li>
          )}
        </ul>
      )}
    </Section>
  );
}

function GivenBack({ c }: { c: Ctx }) {
  const t = c.r.totals;
  const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;
  return (
    <Section
      title="Refunds, voids and write-offs"
      description="Money given back, payments voided, and goods given without payment."
      testId="report-activity"
    >
      <div className="d-kv">
        <KvRow
          label={
            <button type="button" className="rp-link" onClick={() => c.onView('refunds')}>
              Refunds
            </button>
          }
          value={
            <span className="d-mono">{`${n(t.refunds, 'refund', 'refunds')} · ${c.money(t.refunded_minor + t.outside_refunded_minor)}`}</span>
          }
          testId="activity-refunds"
        />
        <KvRow
          label={
            <button type="button" className="rp-link" onClick={() => c.onView('voids')}>
              Payment voids
            </button>
          }
          value={
            <span className="d-mono">{`${n(t.payment_voids, 'void', 'voids')} · ${c.money(t.voided_minor)}`}</span>
          }
          testId="activity-voids"
        />
        <KvRow
          label={
            <button type="button" className="rp-link" onClick={() => c.onView('writeoffs')}>
              Written off
            </button>
          }
          value={<span className="d-mono">{c.money(t.written_off_minor)}</span>}
          testId="activity-writeoffs"
        />
        {t.voided_orders > 0 && (
          <KvRow
            label="Orders voided (not in sales)"
            value={
              <span className="d-mono">{`${n(t.voided_orders, 'order', 'orders')} · ${c.money(t.voided_orders_minor)}`}</span>
            }
            testId="activity-voided-orders"
          />
        )}
      </div>
    </Section>
  );
}

function Detail({ view, c, quiet }: { view: Exclude<View, 'overview'>; c: Ctx; quiet: boolean }) {
  const empty = (title: string) => <EmptyState title={title} testId="report-empty" />;
  switch (view) {
    case 'days': {
      const table = salesByDayTable(c.r);
      return (
        <Section
          title="Sales by day"
          description={SALES_MEANS}
          actions={<CsvButton table={table} c={c} />}
          testId="report-days"
        >
          {c.r.totals.sales_minor !== 0 && <TrendChart c={c} tableId="table-days" />}
          {quiet && empty(emptyTitle(c.live))}
          <ReportTableView table={table} c={c} caption="Sales by day" id="table-days" />
        </Section>
      );
    }
    case 'hours': {
      const table = salesByHourTable(c.r);
      return (
        <Section
          title="Sales by hour"
          description="The period’s sales by the hour each order was placed, in the location’s time; an order from paper counts at the time on the paper."
          actions={<CsvButton table={table} c={c} />}
          testId="report-hours"
        >
          {table.rows.length === 0 ? (
            empty(emptyTitle(c.live))
          ) : (
            <>
              <BarChart
                title="Sales by hour"
                bars={hourBars(c)}
                format={c.money}
                series={['Sales']}
                tableId="table-hours"
                labelHeader="Hour"
              />
              <ReportTableView table={table} c={c} caption="Sales by hour" id="table-hours" />
            </>
          )}
        </Section>
      );
    }
    case 'weekdays': {
      const table = salesByWeekdayTable(c.r);
      const rows = weekdays(c.r.by_weekday);
      return (
        <Section
          title="Sales by weekday"
          description="Each weekday’s sales in the period, and an average day of it (its sales over the number of such days)."
          actions={<CsvButton table={table} c={c} />}
          testId="report-weekdays"
        >
          {c.r.totals.sales_minor === 0 ? (
            empty(emptyTitle(c.live))
          ) : (
            <BarChart
              title="Average day by weekday"
              bars={rows
                .filter((w) => w.days > 0)
                .map((w) => ({ key: w.label, label: w.label, value: w.average_minor ?? 0 }))}
              format={c.money}
              series={['Average day']}
              tableId="table-weekdays"
              labelHeader="Weekday"
            />
          )}
          <ReportTableView table={table} c={c} caption="Sales by weekday" id="table-weekdays" />
        </Section>
      );
    }
    case 'svt':
      return <SalesAndTakings c={c} />;
    case 'methods': {
      const table = methodsTable(c.r);
      return (
        <Section
          title="Payment methods"
          description="Each method over the period’s shifts, as the Day view lists them, then refunds made outside a shift. The net column adds up to the takings."
          actions={<CsvButton table={table} c={c} />}
          testId="report-methods"
        >
          {table.rows.length === 0 ? (
            empty('No payments recorded for this period yet.')
          ) : (
            <ReportTableView table={table} c={c} caption="Payment methods" />
          )}
        </Section>
      );
    }
    case 'refunds': {
      const byMethod = refundsTable(c.r);
      const byDay = refundsByDayTable(c.r);
      return (
        <Section
          title="Refunds"
          description="Money given back to customers. A refund over two payments of one method counts once, as the Day view counts it."
          actions={<CsvButton table={byMethod} c={c} />}
          testId="report-refunds"
        >
          {byMethod.rows.length === 0 ? (
            empty('No refunds in this period.')
          ) : (
            <>
              <ReportTableView table={byMethod} c={c} caption="Refunds by method" />
              <div className="rp-subhead">
                <h3 className="rp-sub">By day</h3>
                <CsvButton table={byDay} c={c} />
              </div>
              <ReportTableView table={byDay} c={c} caption="Refunds by day" />
            </>
          )}
        </Section>
      );
    }
    case 'voids': {
      const byMethod = paymentVoidsTable(c.r);
      const byDay = paymentVoidsByDayTable(c.r);
      return (
        <Section
          title="Payment voids"
          description="Payments taken back on the day of the shift that recorded the void."
          actions={<CsvButton table={byMethod} c={c} />}
          testId="report-voids"
        >
          {byMethod.rows.length === 0 ? (
            empty('No payments voided in this period.')
          ) : (
            <>
              <ReportTableView table={byMethod} c={c} caption="Payment voids by method" />
              <div className="rp-subhead">
                <h3 className="rp-sub">By day</h3>
                <CsvButton table={byDay} c={c} />
              </div>
              <ReportTableView table={byDay} c={c} caption="Payment voids by day" />
            </>
          )}
        </Section>
      );
    }
    case 'writeoffs': {
      const table = writeOffsTable(c.r);
      return (
        <Section
          title="Write-offs"
          description="Walk-outs, staff meals and comps on the day each was recorded; a reinstatement on its own day. Standing is what remains written off."
          actions={<CsvButton table={table} c={c} />}
          testId="report-writeoffs"
        >
          {table.rows.length === 0 ? (
            empty('No write-offs in this period.')
          ) : (
            <ReportTableView table={table} c={c} caption="Write-offs" />
          )}
        </Section>
      );
    }
  }
}
